import apiServices from "../apiServices";
import { validateOrThrowApiResponse } from "../response-validator";

type ChoicePayload = {
    id?: number;
    choice: string;
    is_correct: boolean;
    image_choice: string | null;
    sound_choice?: string | null;
};

type QuestionPayload = {
    no: number;
    question: string;
    hint?: string | null;
    image_question: string | null;
    sound_question?: string | null;
    image_hint?: string | null;
    sound_hint?: string | null;
    skill_listening_speaking: boolean;
    skill_lang_nature: boolean;
    skill_reading: boolean;
    skill_writing: boolean;
    choices: ChoicePayload[];
};

type CreatePayload = {
    gameId: number | string;
    questions: QuestionPayload[];
};

type UpdateQuestionPayload = {
    questionId: number | string;
    no?: number;
    question?: string;
    hint?: string;
    image_question?: string | null;
    sound_question?: string | null;
    image_hint?: string | null;
    sound_hint?: string | null;
    skill_listening_speaking?: boolean;
    skill_lang_nature?: boolean;
    skill_reading?: boolean;
    skill_writing?: boolean;
};

type UpdateChoicePayload = {
    questionId: number | string;
    choices: ChoicePayload[];
};

type CreateChoicePayload = {
    questionId: number | string;
    choices: Omit<ChoicePayload, "id">[];
};

type CreateChoiceResponse = {
    id_question_multiple?: number;
    choices?: Array<{
        id?: number;
        choice?: string | null;
        is_correct?: boolean;
        sound_choice?: string | null;
        image_choice?: string | null;
        create_at?: string;
        update_at?: string;
    }>;
};

type UpdateExerciseNameAndSuggestionPayload = {
    gameId: number | string;
    exercise_name?: string;
    suggestion?: string;
    description?: string;
};

type DeleteQuestionPayload = {
    questionId: number | string;
};

type DeleteChoicePayload = {
    questionId: number | string;
    choiceIds: Array<number | string>;
};

const multipleChoiceAPI = {

    createMultipleChoice(payload: CreatePayload) {
        return apiServices
            .post(`/api/game/${payload.gameId}/questions`, {
                questions: payload.questions,
            }, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error createMultipleChoice:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    updateQuestion(payload: UpdateQuestionPayload) {
        const requestBody: {
            no?: number;
            question?: string;
            hint?: string | null;
            image_question?: string | null;
            sound_question?: string | null;
            image_hint?: string | null;
            sound_hint?: string | null;
            skill_listening_speaking?: boolean;
            skill_lang_nature?: boolean;
            skill_reading?: boolean;
            skill_writing?: boolean;
        } = {};
        if (payload.no !== undefined) {
            requestBody.no = payload.no;
        }
        if (payload.question !== undefined) {
            requestBody.question = payload.question;
        }
        if (payload.hint !== undefined) {
            requestBody.hint = payload.hint;
        }
        if (payload.image_question !== undefined) {
            requestBody.image_question = payload.image_question;
        }
        if (payload.sound_question !== undefined) {
            requestBody.sound_question = payload.sound_question;
        }
        if (payload.image_hint !== undefined) {
            requestBody.image_hint = payload.image_hint;
        }
        if (payload.sound_hint !== undefined) {
            requestBody.sound_hint = payload.sound_hint;
        }
        if (payload.skill_listening_speaking !== undefined) {
            requestBody.skill_listening_speaking = payload.skill_listening_speaking;
        }
        if (payload.skill_lang_nature !== undefined) {
            requestBody.skill_lang_nature = payload.skill_lang_nature;
        }
        if (payload.skill_reading !== undefined) {
            requestBody.skill_reading = payload.skill_reading;
        }
        if (payload.skill_writing !== undefined) {
            requestBody.skill_writing = payload.skill_writing;
        }
        return apiServices
            .put(`/api/questions/${payload.questionId}`, requestBody, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error updateQuestion:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    updateChoice(payload: UpdateChoicePayload) {
        return apiServices
            .put(`/api/questions/${payload.questionId}/choices`, {
                choices: payload.choices,
            }, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error updateChoice:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    createChoice(payload: CreateChoicePayload) {
        return apiServices
            .post(`/api/questions/${payload.questionId}/choices`, {
                choices: payload.choices,
            }, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse<CreateChoiceResponse>(res))
            .catch((err) => {
                console.log("Error createChoice:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    updateExerciseNameAndSuggestion(payload: UpdateExerciseNameAndSuggestionPayload) {
        const requestBody: { exercise_name?: string; suggestion?: string; description?: string } = {};
        if (payload.exercise_name !== undefined) {
            requestBody.exercise_name = payload.exercise_name;
        }
        if (payload.suggestion !== undefined) {
            requestBody.suggestion = payload.suggestion;
        }
        if (payload.description !== undefined) {
            requestBody.description = payload.description;
        }

        return apiServices
            .patch(`/api/game/${payload.gameId}/exerciseNameAndSuggestion`, requestBody, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error updateExerciseNameAndSuggestion:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    insertImageGame(payload: { gameId: number | string; file: File | Blob; filename?: string }) {
        const formData = new FormData();
        if (payload.filename) {
            formData.append("other_image", payload.file, payload.filename);
        } else {
            formData.append("other_image", payload.file as any);
        }

        return apiServices
            .patch(`/api/game/${payload.gameId}/other-image`, formData, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error insertImageGame:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    deleteQuestion(payload: DeleteQuestionPayload) {
        return apiServices
            .delete(`/api/questions/${payload.questionId}`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error deleteQuestion:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    deleteChoice(payload: DeleteChoicePayload) {
        return apiServices
            .post(`/api/questions/${payload.questionId}/choices/delete`, {
                choiceIds: payload.choiceIds,
            }, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error deleteChoice:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    deleteOtherImageGame(payload: { gameId: number | string }) {
        return apiServices
            .delete(`/api/game/${payload.gameId}/other-image`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error deleteOtherImageGame:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

}

export default multipleChoiceAPI;